import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Chip
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CONFIG } from '../../config/constants';
import { getRoleDisplayName, getRoleColor } from '../../config/roles';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DescriptionIcon from '@mui/icons-material/Description';
import QuizIcon from '@mui/icons-material/Quiz';
import HistoryIcon from '@mui/icons-material/History';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import AIConfigurationPage from '../../pages/admin/AIConfiguration';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';

const Navbar: React.FC = () => {
  const { user, isAuthenticated, logout, canCreateQuizzes, canManageUsers, canViewDocuments } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [openAIDialog, setOpenAIDialog] = React.useState(false);

  // Debug log để kiểm tra user data
  React.useEffect(() => {
    if (user) {
      console.log('Current user:', user);
      console.log('User role:', user.role);
      console.log('Can create quizzes:', canCreateQuizzes());
      console.log('Can manage users:', canManageUsers());
      console.log('Can view documents:', canViewDocuments());
    }
  }, [user, canCreateQuizzes, canManageUsers, canViewDocuments]);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
    navigate('/login');
  };

  const handleProfile = () => {
    handleClose();
    navigate('/profile');
  };

  const handleOpenAIDialog = () => {
    setOpenAIDialog(true);
    handleClose();
  };
  const handleCloseAIDialog = () => setOpenAIDialog(false);

  const getAvatarSrc = () => {
    if (user?.avatar) {
      // If avatar path starts with 'http', use as is, otherwise prepend base URL
      return user.avatar.startsWith('http') 
        ? user.avatar 
        : `${CONFIG.API.STORAGE_URL}/${user.avatar}`;
    }
    return '';
  };

  if (!isAuthenticated) {
    return (
      <AppBar position="static">
        <Container maxWidth="lg">
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Question Generator
            </Typography>
            <Button color="inherit" component={RouterLink} to="/login">
              Đăng nhập
            </Button>
            <Button color="inherit" component={RouterLink} to="/register">
              Đăng ký
            </Button>
          </Toolbar>
        </Container>
      </AppBar>
    );
  }

  return (
    <AppBar position="static">
      <Container maxWidth="lg">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Question Generator
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Navigation buttons */}
            {/* Show Upload button only for admin and teacher */}
            {canCreateQuizzes() && (
              <Button
                color="inherit"
                component={RouterLink}
                to="/documents/upload"
                startIcon={<UploadFileIcon />}
              >
                Upload
              </Button>
            )}

            {canViewDocuments() && (
              <Button
                color="inherit"
                component={RouterLink}
                to="/documents"
                startIcon={<DescriptionIcon />}
              >
                Tài liệu
              </Button>
            )}

            <Button
              color="inherit"
              component={RouterLink}
              to="/quizzes"
              startIcon={<QuizIcon />}
            >
              Quiz
            </Button>

            {/* Show Create Quiz button only for admin and teacher */}
            {canCreateQuizzes() && (
              <Button
                color="inherit"
                component={RouterLink}
                to="/quiz/create"
                variant="outlined"
                sx={{ 
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                Tạo Quiz
              </Button>
            )}

            <Button
              color="inherit"
              component={RouterLink}
              to="/quiz/history"
              startIcon={<HistoryIcon />}
            >
              Lịch sử
            </Button>

            {/* Show User Management button only for admin */}
            {canManageUsers() && (
              <Button
                color="inherit"
                component={RouterLink}
                to="/admin/users"
                startIcon={<PeopleIcon />}
              >
                Quản lý TV
              </Button>
            )}

            {/* Show AI Configuration button only for admin */}
            {/* This button is now in the dropdown */}

            {/* User info and menu */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2}}>
              {/* Always show role if user exists */}
              {user && (
                <Chip 
                  label={`${getRoleDisplayName(user.role)} (${user.role})`}
                  size="small" 
                  color={getRoleColor(user.role)}
                  variant="outlined"
                  sx={{ 
                    pointerEvents: 'none',
                    color: 'white', 
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    '& .MuiChip-label': { color: 'white' }
                  }}
                />
              )}
              
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                <Avatar 
                  src={getAvatarSrc()} 
                  alt={user?.name}
                  sx={{ width: 32, height: 32 }}
                >
                  {user?.name?.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
              
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem onClick={handleProfile}>Thông tin cá nhân</MenuItem>
                {canManageUsers() && (
                  <MenuItem onClick={handleOpenAIDialog}>
                    <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
                    Cấu hình AI
                  </MenuItem>
                )}
                <MenuItem onClick={handleLogout}>Đăng xuất</MenuItem>
              </Menu>
            </Box>
          </Box>
        </Toolbar>
      </Container>
      {/* Dialog cấu hình AI cho admin */}
      {canManageUsers() && (
        <Dialog open={openAIDialog} onClose={handleCloseAIDialog} maxWidth="md" fullWidth>
          <DialogTitle>Cấu hình AI</DialogTitle>
          <DialogContent>
            <AIConfigurationPage />
          </DialogContent>
        </Dialog>
      )}
    </AppBar>
  );
};

export default Navbar; 